import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Quorum (by ADCG) — human-in-the-loop agentic scoring: agents propose a score, the analyst decides.
// Tabbed app: Overview (company score cards, agent→analyst) · Companies (sortable table + detail) ·
// Admin (superuser: parameters, scorers & teams + token management, access links). Access-scoped by
// the backend: admin (superuser) sees ALL; a linked platform user sees their scorer + same-team
// scores; an unlinked non-admin sees nothing. Scores arrive from the external agent deck via a
// per-scorer ingest code; scorers/tokens are now managed from the Admin tab.

const GOLD = 'rgb(var(--brand-gold-rgb))';

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s; }
}

// 0–5 score: >=3.5 green, 2.5–3.5 gold, <2.5 red (null → muted).
function scoreColor(v) {
  if (v === null || v === undefined) return 'text-gray-400 dark:text-gray-500';
  if (v >= 3.5) return 'text-green-600 dark:text-green-400';
  if (v >= 2.5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function Label({ children }) {
  return (
    <span className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {children}
    </span>
  );
}

function Section({ title, right, children }) {
  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900/40">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-serif text-lg text-gray-900 dark:text-gray-100">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

// A value is a usable score ONLY if it's a finite number. `overall` can arrive as {} (empty dict)
// and `overall_score_final` as null — guarding here is what prevents ({}).toFixed() from blanking
// the whole page.
const numOrNull = (v) => (typeof v === 'number' && isFinite(v) ? v : null);

// Mean AI vs human across a snapshot's pool scores — the "agents propose, analyst decides" delta.
function aiHumanAvg(pools) {
  const ai = [], hu = [];
  (Array.isArray(pools) ? pools : []).forEach((p) => {
    if (numOrNull(p && p.score_ai) !== null) ai.push(p.score_ai);
    if (numOrNull(p && p.score_human) !== null) hu.push(p.score_human);
  });
  const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
  return { ai: mean(ai), human: mean(hu) };
}

// The overall to display: the stored final if it's a real number, else the analyst (human) pool mean.
// NEVER the raw `overall` field (can be {}).
const POOL_LABELS = { structural: 'Structural', security_overlay: 'Security Overlay' };
const poolLabel = (p) => POOL_LABELS[p] || String(p || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtScore = (v) => { const n = numOrNull(v); return n == null ? '—' : n.toFixed(2); };

// "agent X → analyst Y" — the human-in-the-loop pair. A dot flags where they diverge ≥0.5.
function ScorePair({ ai, human }) {
  const a = numOrNull(ai), h = numOrNull(human);
  const diverge = a != null && h != null && Math.abs(a - h) >= 0.5;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
      <span className="text-gray-400">agent</span><b className={`tabular-nums ${scoreColor(a)}`}>{fmtScore(ai)}</b>
      <span className="text-gray-300 dark:text-gray-600">→</span>
      <span className="text-gray-400">analyst</span><b className={`tabular-nums ${scoreColor(h)}`}>{fmtScore(human)}</b>
      {diverge && <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" title="agent & analyst diverge ≥0.5" />}
    </span>
  );
}

// 0–5 track: gold fill = analyst score, grey dot = agent score (agent proposes, analyst decides).
// 0–max track: gold fill = analyst value, grey dot = agent value. `max` scales it (5 for scores, or the
// row-set max for weight/contribution, so relative magnitudes read at a glance).
function Bar({ ai, human, max = 5 }) {
  const pct = (v) => { const n = numOrNull(v); return n == null ? null : Math.max(2, Math.min(100, (n / (max || 5)) * 100)); };
  const pa = pct(ai), ph = pct(human);
  return (
    <div className="relative h-1.5 w-full rounded bg-gray-100 dark:bg-gray-800">
      {ph != null && <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${ph}%`, background: GOLD }} />}
      {pa != null && <div className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-gray-400 dark:border-gray-900"
        style={{ left: `${pa}%` }} title="agent" />}
    </div>
  );
}

// Interactive metric lens for the drill-down: what the bars/values/sort show.
//   score        — the 0–5 assessment
//   weight       — the decision weight (agent vs analyst — the RE-WEIGHTING, the human-in-the-loop move)
//   contribution — score × weight (what actually drives the pool score; sums ≈ the pool score)
export const METRICS = [{ key: 'score', label: 'Score' }, { key: 'weight', label: 'Weight' }, { key: 'contribution', label: 'Contribution' }];
const sumW = (subs, key) => (subs || []).reduce((t, s) => t + (numOrNull(s[key]) || 0), 0);
function metricSub(s, mode) {
  if (mode === 'weight') return { ai: numOrNull(s.decision_weight_ai), human: numOrNull(s.decision_weight_human) };
  if (mode === 'contribution') {
    const c = (a, b) => { const x = numOrNull(a), y = numOrNull(b); return x != null && y != null ? x * y : null; };
    return { ai: c(s.score_ai, s.decision_weight_ai), human: c(s.score_human, s.decision_weight_human) };
  }
  return { ai: numOrNull(s.score_ai), human: numOrNull(s.score_human) };
}
function metricParam(p, subs, mode) {
  if (mode === 'weight') return { ai: sumW(subs, 'decision_weight_ai'), human: sumW(subs, 'decision_weight_human') };
  if (mode === 'contribution') return { ai: (numOrNull(p.score_ai) || 0) * sumW(subs, 'decision_weight_ai'), human: (numOrNull(p.score_human) || 0) * sumW(subs, 'decision_weight_human') };
  return { ai: numOrNull(p.score_ai), human: numOrNull(p.score_human) };
}
const fmtMetric = (v, mode) => (numOrNull(v) == null ? '—' : mode === 'weight' ? `${(v * 100).toFixed(1)}%` : v.toFixed(2));

// agent → analyst for any lens: scores get the RAG color; weight/contribution get grey → gold.
function MetricPair({ ai, human, mode }) {
  const isScore = mode === 'score' || mode == null;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
      <span className="text-gray-400">agent</span>
      <b className={`tabular-nums ${isScore ? scoreColor(numOrNull(ai)) : 'text-gray-500 dark:text-gray-400'}`}>{fmtMetric(ai, mode)}</b>
      <span className="text-gray-300 dark:text-gray-600">→</span>
      <span className="text-gray-400">analyst</span>
      <b className={`tabular-nums ${isScore ? scoreColor(numOrNull(human)) : ''}`} style={isScore ? undefined : { color: GOLD }}>{fmtMetric(human, mode)}</b>
    </span>
  );
}

function PoolPill({ p }) {
  return (
    <div className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800">
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400">{poolLabel(p.pool)}</div>
      <div className="mt-0.5 flex items-baseline gap-1.5">
        <span className={`font-serif text-xl tabular-nums ${scoreColor(numOrNull(p.score_human))}`}>{fmtScore(p.score_human)}</span>
        <span className="text-[10px] text-gray-400">analyst · agent {fmtScore(p.score_ai)}</span>
      </div>
    </div>
  );
}

/* ── Scorecard detail: pool → parameter → subparameter drill-down ──────────── */
function SubparamRow({ s, mode, max }) {
  const w = numOrNull(s.decision_weight_human) ?? numOrNull(s.decision_weight_ai);
  const mat = numOrNull(s.raw_materiality_human) ?? numOrNull(s.raw_materiality_ai);
  const m = metricSub(s, mode);
  return (
    <div className="grid grid-cols-[1fr_10rem] items-center gap-3 py-1.5 pl-4 pr-2 text-xs">
      <div className="min-w-0">
        <div className="truncate text-gray-700 dark:text-gray-300">{s.label || s.key}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-400">
          <span className="font-mono">{s.key}</span>
          {mat != null && <span>materiality {mat}/5</span>}
          {w != null && <span>weight {(w * 100).toFixed(1)}%</span>}
          {(s.maps_to || []).length > 1 && <span>maps to {s.maps_to.join(', ')}</span>}
        </div>
      </div>
      <div><Bar ai={m.ai} human={m.human} max={mode === 'score' ? 5 : max} /><div className="mt-1 text-right"><MetricPair ai={m.ai} human={m.human} mode={mode} /></div></div>
    </div>
  );
}

function ParamRow({ p, subs, mode, max }) {
  const [open, setOpen] = useState(false);
  const m = metricParam(p, subs, mode);
  // Sort the subparameters by the active lens (analyst value, desc) so the biggest driver is on top,
  // and scale their bars to the largest metric in this parameter.
  const ordered = useMemo(() => {
    const val = (s) => numOrNull(metricSub(s, mode).human) ?? -1;
    return [...subs].sort((a, b) => val(b) - val(a));
  }, [subs, mode]);
  const subMax = Math.max(0.0001, ...ordered.map((s) => numOrNull(metricSub(s, mode).human) || 0),
    ...ordered.map((s) => numOrNull(metricSub(s, mode).ai) || 0));
  return (
    <div className="border-t border-gray-100 dark:border-gray-800/60">
      <button onClick={() => setOpen((v) => !v)} className="grid w-full grid-cols-[1fr_auto] items-center gap-3 py-2 text-left">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-gray-400">{p.key}</span>
            <span className="truncate text-sm text-gray-800 dark:text-gray-200">{p.label}</span>
          </div>
          <div className="mt-1"><Bar ai={m.ai} human={m.human} max={mode === 'score' ? 5 : max} /></div>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <MetricPair ai={m.ai} human={m.human} mode={mode} />
          <span className="text-[10px] text-gray-400">{subs.length} sub {open ? '▾' : '▸'}</span>
        </div>
      </button>
      {open && ordered.length > 0 && (
        <div className="mb-2 rounded-lg bg-gray-50 dark:bg-gray-800/30">{ordered.map((s, i) => <SubparamRow key={i} s={s} mode={mode} max={subMax} />)}</div>
      )}
    </div>
  );
}

export function PoolSection({ pool, params, subs, mode }) {
  const subsFor = (p) => subs.filter((s) => (s.declared_under || (s.maps_to || [])[0]) === p.key);
  const ps = useMemo(() => {
    const rows = params.filter((p) => p.pool === pool.pool);
    const val = (p) => numOrNull(metricParam(p, subsFor(p), mode).human) ?? -1;
    return [...rows].sort((a, b) => val(b) - val(a));
  }, [params, subs, pool.pool, mode]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pmax = Math.max(0.0001, ...ps.flatMap((p) => {
    const m = metricParam(p, subsFor(p), mode); return [numOrNull(m.human) || 0, numOrNull(m.ai) || 0];
  }));
  return (
    <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">{poolLabel(pool.pool)}</h3>
          <div className="text-[11px] text-gray-400">{pool.parameter_range} · {pool.linked_subparameter_count} subparameters · {ps.length} parameters</div>
        </div>
        <div className="text-right">
          <div className={`font-serif text-2xl tabular-nums ${scoreColor(numOrNull(pool.score_human))}`}>{fmtScore(pool.score_human)}</div>
          <div className="text-[11px] text-gray-400">analyst · agent {fmtScore(pool.score_ai)}</div>
        </div>
      </div>
      <div className="mt-3">
        {ps.map((p, i) => <ParamRow key={i} p={p} subs={subsFor(p)} mode={mode} max={pmax} />)}
      </div>
    </section>
  );
}

function ScorecardDetail({ row, onClose }) {
  const [mode, setMode] = useState('score');
  if (!row) return null;
  const pools = Array.isArray(row.pool_scores) ? row.pool_scores : [];
  const params = Array.isArray(row.parameters) ? row.parameters : [];
  const subs = Array.isArray(row.subparameters) ? row.subparameters : [];
  const src = (row.meta || {}).source_scorecard_id;
  const lens = { score: '0–5 assessment', weight: 'decision weight — the analyst re-weights, not just re-scores',
    contribution: 'score × weight — what actually drives each pool score' }[mode];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-2xl text-gray-900 dark:text-gray-100">{row.ticker}
              {row.security_name ? <span className="ml-2 text-base font-normal text-gray-400">{row.security_name}</span> : null}</h2>
            <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-gray-400">
              <span>{row.scored_by_name}</span>{row._team ? <span>· {row._team}</span> : null}
              <span>· {fmtDate(row.known_at)}</span>{row.method_version ? <span>· method {row.method_version}</span> : null}
              {src ? <span>· {src}</span> : null}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
        </div>
        <p className="mt-2 text-[11px] italic text-gray-400">Pools are scored independently — no blended overall (by design).</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs dark:border-gray-800">
            {METRICS.map((mt) => (
              <button key={mt.key} onClick={() => setMode(mt.key)}
                className="rounded-md px-2.5 py-1 font-medium transition-colors"
                style={mode === mt.key ? { background: GOLD, color: '#111' } : { color: '#6b7280' }}>{mt.label}</button>
            ))}
          </div>
          <span className="text-[11px] text-gray-400">{lens}</span>
        </div>
        <div className="mt-4 flex flex-col gap-4">
          {pools.map((pool, i) => <PoolSection key={i} pool={pool} params={params} subs={subs} mode={mode} />)}
        </div>
      </div>
    </div>
  );
}

function Tabs({ tabs, active, onChange }) {
  return (
    <nav className="mb-5 flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-800">
      {tabs.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className="-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors"
          style={active === t.key ? { borderColor: GOLD, color: GOLD }
            : { borderColor: 'transparent', color: '#6b7280' }}>
          {t.label}{t.count != null ? <span className="ml-1 text-xs text-gray-400">{t.count}</span> : null}
        </button>
      ))}
    </nav>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">{label}</div>
      <div className={`mt-1 font-serif text-2xl tabular-nums ${color || 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
    </div>
  );
}

function QuorumCard({ r, onOpen }) {
  const pools = Array.isArray(r.pool_scores) ? r.pool_scores : [];
  return (
    <button onClick={() => onOpen(r)}
      className="flex flex-col rounded-xl border border-gray-200 p-4 text-left transition-all hover:border-brand-gold hover:shadow-sm dark:border-gray-800">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-serif text-lg text-gray-900 dark:text-gray-100">{r.ticker}</div>
          {r.security_name ? <div className="truncate text-xs text-gray-400">{r.security_name}</div> : null}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-gray-300 dark:text-gray-600">{pools.length} pools</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {pools.map((p, i) => <PoolPill key={i} p={p} />)}
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-gray-400">
        <span className="truncate">{r.scored_by_name || '—'}{r._team ? ` · ${r._team}` : ''}</span>
        <span className="whitespace-nowrap">{fmtDate(r.known_at)}</span>
      </div>
    </button>
  );
}

function OverviewTab({ rows, onOpen }) {
  if (!rows.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No scorecards yet — they appear here as scorers publish.</p>;
  const companies = new Set(rows.map((r) => r.ticker)).size;
  const scorers = new Set(rows.map((r) => r.scored_by_name).filter(Boolean)).size;
  const teams = new Set(rows.map((r) => r._team).filter(Boolean)).size;
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Companies" value={companies} />
        <Stat label="Scorers" value={scorers} />
        <Stat label="Teams" value={teams} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => <QuorumCard key={r.id} r={r} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

/* ── Companies table (a sortable column per pool present in the data; row → detail) ── */
function ScoresTable({ rows, onOpen }) {
  // Columns are DERIVED from the pools actually present — no hardcoded pool names, so any
  // scorecard shape (2 pools, 5 pools, differently-named) renders correctly.
  const poolNames = useMemo(() => {
    const seen = [];
    rows.forEach((r) => (r.pool_scores || []).forEach((p) => { if (p.pool && !seen.includes(p.pool)) seen.push(p.pool); }));
    return seen;
  }, [rows]);
  const poolHuman = (r, pool) => { const p = (r.pool_scores || []).find((x) => x.pool === pool); return p ? numOrNull(p.score_human) : null; };
  const cols = useMemo(() => [
    { key: 'ticker', type: 'string', get: (r) => r.ticker },
    { key: 'scorer', type: 'string', get: (r) => r.scored_by_name || '' },
    ...poolNames.map((pn) => ({ key: `pool:${pn}`, type: 'number', get: (r) => (poolHuman(r, pn) ?? -1) })),
    { key: 'known_at', type: 'string', get: (r) => r.known_at || '' },
  ], [poolNames]);
  const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'known_at', dir: 'desc' });
  if (!rows.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No scorecards yet.</p>;
  const cell = (r, pool) => { const v = poolHuman(r, pool); return <span className={`tabular-nums ${scoreColor(v)}`}>{fmtScore(v)}</span>; };
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[680px] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <SortableTh label="Ticker" colKey="ticker" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Scorer" colKey="scorer" toggle={toggle} indicator={indicator} align="left" />
            <th className="px-2 py-2 text-left"><Label>Team</Label></th>
            {poolNames.map((pn) => (
              <SortableTh key={pn} label={poolLabel(pn)} colKey={`pool:${pn}`} toggle={toggle} indicator={indicator} align="right" />
            ))}
            <SortableTh label="Known at" colKey="known_at" toggle={toggle} indicator={indicator} align="right" />
            <th className="px-2 py-2 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r)}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40">
              <td className="px-2 py-2 font-medium text-gray-900 dark:text-gray-100">{r.ticker}</td>
              <td className="px-2 py-2 text-gray-700 dark:text-gray-300">{r.scored_by_name || '—'}</td>
              <td className="px-2 py-2 text-gray-500 dark:text-gray-400">{r._team || '—'}</td>
              {poolNames.map((pn) => <td key={pn} className="px-2 py-2 text-right font-semibold">{cell(r, pn)}</td>)}
              <td className="px-2 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmtDate(r.known_at)}</td>
              <td className="px-2 py-2 text-right text-xs" style={{ color: GOLD }}>View →</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Admin: scorers + team + ingest tokens ────────────────────────────────── */
function AddScorerForm({ onCreate }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [team, setTeam] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await onCreate({ external_id: email.trim(), name: name.trim(), team: team.trim() });
      setEmail(''); setName(''); setTeam('');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-900/40">
      <div><Label>Email</Label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="scorer@firm.com"
          className="mt-1 block w-48 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700" /></div>
      <div><Label>Name</Label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
          className="mt-1 block w-40 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700" /></div>
      <div><Label>Team</Label>
        <input value={team} onChange={(e) => setTeam(e.target.value)} placeholder="(optional)"
          className="mt-1 block w-40 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700" /></div>
      <button onClick={submit} disabled={busy || !email.trim()}
        className="rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        style={{ color: GOLD, border: `1px solid ${GOLD}` }}>{busy ? 'Adding…' : 'Add scorer'}</button>
    </div>
  );
}

function TokenManager({ scorer, onChanged }) {
  const [open, setOpen] = useState(false);
  const [tokens, setTokens] = useState(null);
  const [label, setLabel] = useState('');
  const [minted, setMinted] = useState(null);   // plaintext code — shown ONCE
  const [busy, setBusy] = useState(false);
  const loadTokens = async () => { const r = await researchosService.getScorerTokens(scorer.id); setTokens(r.data); };
  const toggle = async () => { const next = !open; setOpen(next); if (next && tokens === null) await loadTokens(); };
  const mint = async () => {
    setBusy(true);
    try {
      const r = await researchosService.mintScorerToken(scorer.id, { name: label.trim() });
      setMinted(r.data.code); setLabel(''); await loadTokens(); onChanged && onChanged();
    } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this token? It stops working immediately (irreversible — mint a new one to rotate).')) return;
    await researchosService.revokeToken(id); await loadTokens(); onChanged && onChanged();
  };
  return (
    <div>
      <button onClick={toggle} className="text-xs font-medium" style={{ color: GOLD }}>
        {open ? 'Hide tokens' : `Tokens (${scorer.token_count})`}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
          {minted && (
            <div className="mb-2 rounded border border-green-300 bg-green-50 p-2 text-xs dark:border-green-800 dark:bg-green-900/20">
              <div className="mb-1 font-medium text-green-700 dark:text-green-300">New code — copy now, shown once (only the hash is stored):</div>
              <code className="break-all text-gray-900 dark:text-gray-100">{minted}</code>
            </div>
          )}
          {tokens !== null && tokens.length === 0 && <p className="text-xs text-gray-400">No tokens yet — mint one below.</p>}
          {(tokens || []).map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b border-gray-100 py-1 text-xs dark:border-gray-800/60">
              <span className="text-gray-600 dark:text-gray-300">
                <code>{t.prefix}…</code>{t.name ? ` · ${t.name}` : ''}{' · '}
                <span className={t.is_active ? 'text-green-600 dark:text-green-400' : 'text-gray-400 line-through'}>
                  {t.is_active ? 'active' : 'revoked'}</span>
                {t.last_used_at ? ` · used ${new Date(t.last_used_at).toLocaleDateString()}` : ' · never used'}
              </span>
              {t.is_active && <button onClick={() => revoke(t.id)} className="font-medium text-red-600 dark:text-red-400">Revoke</button>}
            </div>
          ))}
          <div className="mt-2 flex items-end gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label e.g. ChatGPT prod"
              className="w-44 rounded border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700" />
            <button onClick={mint} disabled={busy}
              className="rounded px-2 py-1 text-xs font-medium disabled:opacity-50"
              style={{ color: GOLD, border: `1px solid ${GOLD}` }}>{busy ? 'Minting…' : 'Mint token'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ScorersAdmin({ scorers, onSave, onCreate, reload }) {
  const [edit, setEdit] = useState({});
  return (
    <div className="flex flex-col gap-3">
      <AddScorerForm onCreate={onCreate} />
      {scorers.map((s) => (
        <div key={s.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.name || s.external_id}</div>
              <div className="text-xs text-gray-400">{s.external_id}</div>
            </div>
            <div className="flex items-end gap-2">
              <div><Label>Team</Label>
                <input className="mt-1 w-36 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700"
                  defaultValue={s.team || ''} placeholder="(none)"
                  onChange={(e) => setEdit((m) => ({ ...m, [s.id]: e.target.value }))} /></div>
              <button className="rounded px-2 py-1.5 text-xs font-medium disabled:opacity-40"
                style={{ color: GOLD, border: `1px solid ${GOLD}` }}
                disabled={edit[s.id] === undefined || edit[s.id] === (s.team || '')}
                onClick={() => onSave(s.id, { team: edit[s.id] })}>Save</button>
              <span className="tabular-nums text-xs text-gray-400">{s.snapshot_count} scores</span>
            </div>
          </div>
          <div className="mt-2"><TokenManager scorer={s} onChanged={reload} /></div>
        </div>
      ))}
    </div>
  );
}

/* ── Admin: parameter review ──────────────────────────────────────────────── */
function ParametersAdmin({ params, onReview }) {
  const badge = (st) => ({
    auto: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    confirmed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  }[st] || 'bg-gray-100 text-gray-600');
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <th className="px-2 py-2 text-left"><Label>Key</Label></th>
            <th className="px-2 py-2 text-left"><Label>Label</Label></th>
            <th className="px-2 py-2 text-left"><Label>Pool</Label></th>
            <th className="px-2 py-2 text-left"><Label>Status</Label></th>
            <th className="px-2 py-2 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800/60">
              <td className="px-2 py-2 font-mono text-gray-900 dark:text-gray-100">{p.key}</td>
              <td className="px-2 py-2 text-gray-700 dark:text-gray-300">{p.label || '—'}</td>
              <td className="px-2 py-2 text-gray-500">{p.pool || '—'}</td>
              <td className="px-2 py-2">
                <span className={`rounded px-2 py-0.5 text-xs ${badge(p.review_status)}`}>{p.review_status}</span>
              </td>
              <td className="px-2 py-2 text-right">
                {p.review_status !== 'confirmed' && (
                  <button className="mr-2 text-xs font-medium text-green-600 dark:text-green-400"
                    onClick={() => onReview(p.id, 'confirm')}>Confirm</button>
                )}
                {p.review_status !== 'rejected' && (
                  <button className="text-xs font-medium text-red-600 dark:text-red-400"
                    onClick={() => onReview(p.id, 'reject')}>Reject</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Admin: platform-user ↔ scorer links ──────────────────────────────────── */
function LinksAdmin({ links, scorers, onCreate, onDelete }) {
  const [userId, setUserId] = useState('');
  const [scorerId, setScorerId] = useState('');
  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div>
          <Label>Platform user ID</Label>
          <input className="mt-1 block w-40 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700"
            value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="e.g. 7" />
        </div>
        <div>
          <Label>Scorer</Label>
          <select className="mt-1 block w-56 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
            value={scorerId} onChange={(e) => setScorerId(e.target.value)}>
            <option value="">— select —</option>
            {scorers.map((s) => <option key={s.id} value={s.id}>{s.external_id} ({s.team || 'no team'})</option>)}
          </select>
        </div>
        <button className="rounded px-3 py-1.5 text-sm font-medium" style={{ color: GOLD, border: `1px solid ${GOLD}` }}
          disabled={!userId || !scorerId}
          onClick={() => { onCreate({ user: Number(userId), research_user: Number(scorerId) }); setUserId(''); setScorerId(''); }}>
          Link
        </button>
      </div>
      {links.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No links yet — linked users see their team's scores.</p>
      ) : (
        <ul className="space-y-1">
          {links.map((l) => (
            <li key={l.id} className="flex items-center justify-between rounded border border-gray-100 px-3 py-1.5 text-sm dark:border-gray-800">
              <span className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">{l.username}</span> → {l.scorer_external_id}
                <span className="text-gray-400"> ({l.scorer_team || 'no team'})</span>
              </span>
              <button className="text-xs text-red-600 dark:text-red-400" onClick={() => onDelete(l.id)}>Remove</button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */
/* ── Sectors: coverage master tiles (sectors covered + # scored stocks) → click to drill ── */
function SectorTile({ s, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-lg border p-3 text-left transition ${active
        ? 'bg-amber-50/60 dark:bg-amber-900/10'
        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900/40 dark:hover:border-gray-700'}`}
      style={active ? { borderColor: GOLD } : undefined}>
      <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{s.sector}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-serif text-2xl tabular-nums text-gray-900 dark:text-gray-100">{s.count}</span>
        <span className="text-[11px] text-gray-400">{s.count === 1 ? 'stock' : 'stocks'}</span>
      </div>
    </button>
  );
}

const SECTOR_NAME_COLS = [
  { key: 'ticker', type: 'string', get: (r) => r.ticker },
  { key: 'name', type: 'string', get: (r) => r.name },
  { key: 'industry', type: 'string', get: (r) => r.industry },
  { key: 'score', type: 'number', get: (r) => r.score },
];

// The drill-down: the scored names within one sector — sortable (house rule), score-colored,
// click a row to open its full scorecard (when that scorecard is in the loaded set).
function SectorNames({ names, onOpen }) {
  const { sorted, toggle, indicator } = useSortableRows(names, SECTOR_NAME_COLS, { key: 'score', dir: 'desc' });
  const th = 'py-1.5 text-[11px] uppercase tracking-wider text-gray-500';
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800">
            <SortableTh label="Ticker" colKey="ticker" toggle={toggle} indicator={indicator} align="left" className={`${th} pr-3`} />
            <SortableTh label="Company" colKey="name" toggle={toggle} indicator={indicator} align="left" className={`${th} pr-3`} />
            <SortableTh label="Industry" colKey="industry" toggle={toggle} indicator={indicator} align="left" className={`${th} pr-3`} />
            <SortableTh label="Score" colKey="score" toggle={toggle} indicator={indicator} align="right" className={`${th} pl-3`} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.ticker} onClick={() => onOpen && onOpen(r.ticker)}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40">
              <td className="py-1.5 pr-3 font-medium text-gray-900 dark:text-gray-100">{r.ticker}</td>
              <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300">{r.name || '—'}</td>
              <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{r.industry || '—'}</td>
              <td className={`py-1.5 pl-3 text-right tabular-nums ${scoreColor(numOrNull(r.score))}`}>{fmtScore(r.score)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectorsTab({ data, onOpenTicker }) {
  const [openSector, setOpenSector] = useState(null);
  if (!data) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading coverage…</p>;
  const sectors = data.sectors || [];
  if (!sectors.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No sector coverage yet — scored companies appear here grouped by sector.</p>;
  const active = sectors.find((s) => s.sector === openSector) || null;
  return (
    <div>
      <div className="mb-3 text-[11px] uppercase tracking-wider text-gray-400">
        {sectors.length} sectors · {data.covered_securities} stocks covered
        {data.uncovered ? ` · ${data.uncovered} without a sector` : ''}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {sectors.map((s) => (
          <SectorTile key={s.sector} s={s} active={s.sector === openSector}
            onClick={() => setOpenSector(s.sector === openSector ? null : s.sector)} />
        ))}
      </div>
      {active && (
        <Section title={`${active.sector} · ${active.count} ${active.count === 1 ? 'stock' : 'stocks'}`}>
          <SectorNames names={active.names} onOpen={onOpenTicker} />
        </Section>
      )}
    </div>
  );
}

export default function TeamScoresPage() {
  const [scope, setScope] = useState(null);
  const [scores, setScores] = useState([]);
  const [scorers, setScorers] = useState([]);
  const [links, setLinks] = useState([]);
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sectors, setSectors] = useState(null);      // sector-coverage master tiles
  const [tab, setTab] = useState('sectors');         // Quorum lands on sector coverage
  const [selected, setSelected] = useState(null);   // scorecard row for the detail modal

  const loadAdmin = useCallback(async () => {
    const [sc, lk, pr] = await Promise.all([
      researchosService.getScorers(),
      researchosService.getLinks(),
      researchosService.getParameters(),
    ]);
    setScorers(sc.data); setLinks(lk.data); setParams(pr.data);
    return sc.data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const sco = (await researchosService.getScope()).data;
      setScope(sco);
      if (sco.is_admin || sco.can_view) {
        const sres = await researchosService.getScores();
        // Collapse the append-only history to ONE row per (company, scorer) for display, but source
        // SCORES and the PRIMER INDEPENDENTLY — each from its newest carrier:
        //   * scores  = the TIP (latest-ingested revision by created_at; known_at can tie after a backfill).
        //   * notebook = the notebook from the newest revision that HAS one — even if that's an OLDER
        //     revision than the score tip. So a later score-only re-push (no notebook) NEVER hides the
        //     primer; you always see the most recent primer available.
        const groups = new Map();
        for (const r of sres.data) {
          const k = `${r.ticker}||${r.scored_by_name}`;
          if (!groups.has(k)) groups.set(k, []);
          groups.get(k).push(r);
        }
        const deduped = [];
        for (const rows of groups.values()) {
          const byNewest = [...rows].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
          const tip = byNewest[0];                                  // latest scores
          const nbRow = byNewest.find((x) => x.notebook);           // newest revision carrying a primer
          deduped.push({ ...tip, notebook: tip.notebook || (nbRow ? nbRow.notebook : null) });
        }
        let scorerMap = {};
        if (sco.is_admin) {
          const sc = await loadAdmin();
          scorerMap = Object.fromEntries(sc.map((s) => [s.external_id, s.team]));
        }
        setScores(deduped.map((r) => ({ ...r, _team: scorerMap[r.scored_by_name] ?? sco.team ?? '' })));
        try { setSectors((await researchosService.getSectorCoverage()).data); }
        catch { setSectors({ sectors: [], covered_securities: 0, uncovered: 0 }); }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load Company Scorecards.');
    } finally {
      setLoading(false);
    }
  }, [loadAdmin]);

  useEffect(() => { load(); }, [load]);

  const saveTeam = async (id, data) => { await researchosService.updateScorer(id, data); await load(); };
  const addScorer = async (data) => {
    try { await researchosService.createScorer(data); await loadAdmin(); }
    catch (e) { alert('Add scorer failed: ' + (e?.response?.data ? JSON.stringify(e.response.data) : e.message)); }
  };
  const reviewParam = async (id, action) => { await researchosService.reviewParameter(id, action); await loadAdmin(); };
  const createLink = async (data) => { try { await researchosService.createLink(data); await loadAdmin(); } catch (e) { alert('Link failed: ' + (e?.response?.data ? JSON.stringify(e.response.data) : e.message)); } };
  const deleteLink = async (id) => { await researchosService.deleteLink(id); await loadAdmin(); };

  const needsReview = params.filter((p) => p.review_status === 'auto');

  if (loading) return <div className="text-gray-500 dark:text-gray-400">Loading Quorum…</div>;

  const header = (
    <div className="mb-5">
      <h1 className="font-serif text-2xl text-gray-900 dark:text-gray-100">
        Quorum <span className="align-middle text-[11px] font-normal uppercase tracking-[0.25em] text-gray-400">by ADCG</span>
      </h1>
      <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
        Where agents propose and the analyst decides
        {scope?.is_admin ? ' · admin view (all scores)' : scope?.team ? ` · team ${scope.team}` : ''}.
      </p>
    </div>
  );

  if (!scope?.is_admin && !scope?.can_view) {
    return (
      <>
        {header}
        <Section title="No access">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Your login isn't linked to a research scorer yet. Ask an administrator to link you so you
            can see your own and your team's scorecards.
          </p>
        </Section>
      </>
    );
  }

  const tabs = [
    { key: 'sectors', label: 'Sectors' },
    { key: 'overview', label: 'Overview' },
    { key: 'companies', label: 'Companies', count: scores.length },
    ...(scope?.is_admin ? [{ key: 'admin', label: 'Admin' }] : []),
  ];

  return (
    <>
      {header}
      {err && <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">{err}</div>}
      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'sectors' && (
        <SectorsTab data={sectors}
          onOpenTicker={(t) => { const row = scores.find((r) => r.raw_ticker === t); if (row) setSelected(row); }} />
      )}

      {tab === 'overview' && <OverviewTab rows={scores} onOpen={setSelected} />}

      {tab === 'companies' && (
        scores.length
          ? <ScoresTable rows={scores} onOpen={setSelected} />
          : <p className="text-sm text-gray-500 dark:text-gray-400">No scorecards yet.</p>
      )}

      {tab === 'admin' && scope?.is_admin && (
        <div className="flex flex-col gap-6">
          <Section title="Parameters — review"
            right={needsReview.length ? <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{needsReview.length} need review</span> : null}>
            <ParametersAdmin params={params} onReview={reviewParam} />
          </Section>
          <Section title="Scorers & teams">
            <ScorersAdmin scorers={scorers} onSave={saveTeam} onCreate={addScorer} reload={loadAdmin} />
          </Section>
          <Section title="User access links">
            <LinksAdmin links={links} scorers={scorers} onCreate={createLink} onDelete={deleteLink} />
          </Section>
        </div>
      )}

      <ScorecardDetail row={selected} onClose={() => setSelected(null)} />
    </>
  );
}
